import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Card } from '../cards/Card';
import { Button } from '../buttons/Button';
import { Upload, X, Loader2, Star, Trash2, MapPin, Navigation, Search, CheckCircle, ExternalLink, RefreshCw, Save, Plus, Edit2, UserCheck } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { GoogleGenAI, Type } from "@google/genai";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Property, RoomDefinition, UserRole } from '../../types';

// Red Leaflet marker icon
const redIcon = L.icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  iconRetinaUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

// Grey/Default Leaflet marker icon
const defaultIcon = L.icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28],
  shadowSize: [41, 41]
});

const AMENITIES_LIST = [
  'Wifi', 'Parking', 'Security', 'Pool', 'Gym',
  'Furnished', 'Air Conditioning', 'Heating',
  'Laundry', 'Balcony', 'Pet Friendly', 'Near Bus Stop'
];

// --- Map Helper Components ---
const DraggableMarker = ({ position, onDragEnd }: { position: L.LatLngExpression, onDragEnd: (lat: number, lng: number) => void }) => {
  const markerRef = useRef<L.Marker>(null);
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const { lat, lng } = marker.getLatLng();
          onDragEnd(lat, lng);
        }
      },
    }),
    [onDragEnd],
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={redIcon}
    />
  );
};

// Handle Click Events on Map to Place Pin
const MapEventsHandler = ({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const MapUpdater = ({ center }: { center: [number, number] }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo(center, map.getZoom());
  }, [center, map]);
  return null;
};

interface PropertyFormProps {
  onSuccess: () => void;
  initialData?: Property | null; // Optional prop for edit mode
}

export const PropertyForm: React.FC<PropertyFormProps> = ({ onSuccess, initialData }) => {
  const { addProperty, updateProperty, users } = useData();
  const { user } = useAuth();

  // --- Form State ---
  const [formData, setFormData] = useState({
    title: '', location: '', locationName: '', price: '', type: 'Studio', bedrooms: '1', bathrooms: '1', description: '',
    latitude: '', longitude: '',
    coAgentId: ''
  });

  // --- Rooms State ---
  const [rooms, setRooms] = useState<RoomDefinition[]>([]);
  const [newRoom, setNewRoom] = useState({
    name: '',
    price: '',
    description: '',
    pricingType: 'per_room' as 'per_room' | 'per_bed'
  });
  const [beds, setBeds] = useState<{ name: string, price: string }[]>([]);
  const [editingRoomId, setEditingRoomId] = useState<string | null>(null);

  // --- Property Beds (General / Not tied to room) ---
  const [propertyBeds, setPropertyBeds] = useState<{ id: string, name: string, price: string, available: boolean }[]>([]);
  const [newPropBed, setNewPropBed] = useState({ name: '', price: '' });

  // --- Upload State Machine ---
  type UploadStatus = 'idle' | 'uploading_images' | 'saving_data' | 'success' | 'error';
  const [status, setStatus] = useState<UploadStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [createdPropertyId, setCreatedPropertyId] = useState<string | null>(null);

  // Default Map Center
  const defaultCenter: [number, number] = [-26.2041, 28.0473];
  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter);
  const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);

  // Image State (Can be existing URL or new File)
  const [images, setImages] = useState<{ file: File | null, url: string }[]>([]);
  const [primaryIndex, setPrimaryIndex] = useState(0);
  const [isLocating, setIsLocating] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Autocomplete State
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const searchTimeout = useRef<any>(null);

  // --- INITIALIZATION FOR EDIT MODE ---
  useEffect(() => {
    if (initialData) {
      setFormData({
        title: initialData.title,
        location: initialData.location,
        price: initialData.price.toString(),
        type: initialData.type,
        bedrooms: initialData.bedrooms.toString(),
        bathrooms: initialData.bathrooms.toString(),
        description: initialData.description,
        locationName: initialData.locationName || '',
        latitude: initialData.latitude?.toString() || '',
        longitude: initialData.longitude?.toString() || '',
        coAgentId: initialData.coAgentId || ''
      });
      setSelectedAmenities(initialData.amenities);
      setRooms(initialData.rooms || []);
      setPropertyBeds(initialData.beds?.map(b => ({ ...b, price: b.price.toString() })) || []);

      const initialImages = initialData.images?.map(url => ({ file: null, url })) || [];
      if (initialImages.length === 0 && initialData.image) {
        initialImages.push({ file: null, url: initialData.image });
      }
      setImages(initialImages);
      setPrimaryIndex(0);

      // Map Center
      if (initialData.latitude && initialData.longitude) {
        setMapCenter([initialData.latitude, initialData.longitude]);
      }
    }
  }, [initialData]);

  // Sync Form Lat/Lng to Map Center
  useEffect(() => {
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        // Only update map center if significant change to avoid jitter during drag
      }
    }
  }, [formData.latitude, formData.longitude]);

  // --- Handlers ---

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files) as File[];
      const remainingSlots = 10 - images.length;
      if (newFiles.length > remainingSlots) {
        alert(`You can only add ${remainingSlots} more images (Max 10).`);
        return;
      }
      const newImages = newFiles.map(file => ({
        file,
        url: URL.createObjectURL(file)
      }));
      setImages(prev => [...prev, ...newImages]);
    }
  };

  const removeImage = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    if (index === primaryIndex) setPrimaryIndex(0);
    else if (index < primaryIndex) setPrimaryIndex(primaryIndex - 1);
  };

  const toggleAmenity = (amenity: string) => {
    setSelectedAmenities(prev =>
      prev.includes(amenity) ? prev.filter(a => a !== amenity) : [...prev, amenity]
    );
  };

  const handleAddRoom = () => {
    if (!newRoom.name) return;
    if (newRoom.pricingType === 'per_room' && !newRoom.price) return;
    if (newRoom.pricingType === 'per_bed' && (beds.length === 0 || beds.some(b => !b.name || !b.price))) {
      alert("Please add at least one bed with a name and price.");
      return;
    }

    const roomPrice = newRoom.pricingType === 'per_room'
      ? parseFloat(newRoom.price)
      : Math.min(...beds.map(b => parseFloat(b.price)));

    setRooms(prev => [...prev, {
      id: `room-${Date.now()}`,
      name: newRoom.name,
      price: roomPrice,
      description: newRoom.description,
      available: true,
      pricingType: newRoom.pricingType,
      beds: newRoom.pricingType === 'per_bed' ? beds.map((b, i) => ({
        id: `bed-${Date.now()}-${i}`,
        name: b.name,
        price: parseFloat(b.price),
        available: true
      })) : undefined
    }]);
    setNewRoom({ name: '', price: '', description: '', pricingType: 'per_room' });
    setBeds([]);
  };

  const handleEditRoom = (room: RoomDefinition) => {
    setNewRoom({
      name: room.name,
      price: room.price.toString(),
      description: room.description,
      pricingType: room.pricingType
    });
    setBeds(room.beds ? room.beds.map(b => ({ name: b.name, price: b.price.toString() })) : []);
    setEditingRoomId(room.id);
  };

  const handleUpdateRoom = () => {
    if (!editingRoomId || !newRoom.name) return;
    if (newRoom.pricingType === 'per_room' && !newRoom.price) return;
    if (newRoom.pricingType === 'per_bed' && (beds.length === 0 || beds.some(b => !b.name || !b.price))) {
      alert("Please add at least one bed with a name and price.");
      return;
    }

    const roomPrice = newRoom.pricingType === 'per_room'
      ? parseFloat(newRoom.price)
      : Math.min(...beds.map(b => parseFloat(b.price)));

    setRooms(prev => prev.map(r => r.id === editingRoomId ? {
      ...r,
      name: newRoom.name,
      price: roomPrice,
      description: newRoom.description,
      pricingType: newRoom.pricingType,
      beds: newRoom.pricingType === 'per_bed' ? beds.map((b, i) => ({
        id: `bed-${editingRoomId}-${i}`,
        name: b.name,
        price: parseFloat(b.price),
        available: true
      })) : undefined
    } : r));
    setNewRoom({ name: '', price: '', description: '', pricingType: 'per_room' });
    setBeds([]);
    setEditingRoomId(null);
  };

  const handleAddBed = () => {
    setBeds(prev => [...prev, { name: `Bed ${prev.length + 1}`, price: newRoom.price || '' }]);
  };

  const handleRemoveBed = (index: number) => {
    setBeds(prev => prev.filter((_, i) => i !== index));
  };

  const handleBedChange = (index: number, field: 'name' | 'price', value: string) => {
    setBeds(prev => prev.map((b, i) => i === index ? { ...b, [field]: value } : b));
  };

  const handleRemoveRoom = (id: string) => {
    setRooms(prev => prev.filter(r => r.id !== id));
    if (editingRoomId === id) {
      setEditingRoomId(null);
      setNewRoom({ name: '', price: '', description: '', pricingType: 'per_room' });
    }
  };

  // --- Property Beds Helpers ---
  const handleAddPropBed = () => {
    if (!newPropBed.name || !newPropBed.price) return;
    const bed = {
      id: `bed-${Date.now()}`,
      name: newPropBed.name,
      price: newPropBed.price,
      available: true
    };
    setPropertyBeds(prev => [...prev, bed]);
    setNewPropBed({ name: '', price: '' });
  };

  const handleRemovePropBed = (id: string) => {
    setPropertyBeds(prev => prev.filter(b => b.id !== id));
  };

  const togglePropBedAvailability = (id: string) => {
    setPropertyBeds(prev => prev.map(b => b.id === id ? { ...b, available: !b.available } : b));
  };

  // Reverse Geocoding Function
  const reverseGeocode = async (lat: number, lng: number) => {
    setIsReverseGeocoding(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: `What is the closest street address for coordinates ${lat}, ${lng}? Return ONLY the address string as a single line. Do not include quotes.`,
      });
      const address = response.text?.trim();
      if (address) {
        setFormData(prev => ({ ...prev, location: address }));
      }
    } catch (error) {
      console.error("Reverse geocoding failed:", error);
    } finally {
      setIsReverseGeocoding(false);
    }
  };

  const handleAddressInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setFormData({ ...formData, location: val });
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!val || val.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    searchTimeout.current = setTimeout(() => {
      fetchSuggestions(val);
    }, 500);
  };

  const fetchSuggestions = async (query: string) => {
    setIsSuggesting(true);
    try {
      const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
      const prompt = `User typed address: "${query}". Provide 5 likely real-world address suggestions (focus on South Africa if ambiguous). Return strictly JSON: [{ "address": "Full Address string", "lat": number, "lng": number }]`;
      const response = await ai.models.generateContent({
        model: 'gemini-2.5-flash',
        contents: prompt,
        config: {
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.ARRAY,
            items: {
              type: Type.OBJECT,
              properties: {
                address: { type: Type.STRING },
                lat: { type: Type.NUMBER },
                lng: { type: Type.NUMBER }
              }
            }
          }
        }
      });
      const json = JSON.parse(response.text || '[]');
      setSuggestions(json);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Autocomplete error:", error);
    } finally {
      setIsSuggesting(false);
    }
  };

  const selectSuggestion = (s: any) => {
    setFormData(prev => ({
      ...prev,
      location: s.address,
      latitude: s.lat.toString(),
      longitude: s.lng.toString()
    }));
    setMapCenter([s.lat, s.lng]);
    setShowSuggestions(false);
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        setFormData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
        setMapCenter([lat, lng]);
        reverseGeocode(lat, lng);
        setIsLocating(false);
      },
      (error) => {
        console.error(error);
        alert("Unable to retrieve your location.");
        setIsLocating(false);
      }
    );
  };

  const handleMarkerDragEnd = (lat: number, lng: number) => {
    setFormData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
    reverseGeocode(lat, lng);
  };

  const handleMapClickHandler = (lat: number, lng: number) => {
    setFormData(prev => ({ ...prev, latitude: lat.toString(), longitude: lng.toString() }));
    reverseGeocode(lat, lng);
  };

  // Helper function to upload with timeout
  const uploadImageWithTimeout = async (file: File, path: string) => {
    const uploadPromise = supabase.storage
      .from('property-images')
      .upload(path, file, {
        cacheControl: '3600',
        upsert: true
      });

    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Upload timed out. Please check your internet connection.')), 15000);
    });

    return Promise.race([uploadPromise, timeoutPromise]) as Promise<{ data: any; error: any }>;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (images.length === 0) {
      alert("Please upload at least one image.");
      return;
    }

    setErrorMessage('');
    setStatus('uploading_images');

    try {
      // 1. Upload Images
      const uploadedUrls: string[] = [];
      const orderedImages = [
        images[primaryIndex],
        ...images.filter((_, i) => i !== primaryIndex)
      ];

      for (let i = 0; i < orderedImages.length; i++) {
        const img = orderedImages[i];

        if (img.file) {
          setStatusMessage(`Uploading image ${i + 1}...`);
          const fileExt = img.file.name.split('.').pop() || 'jpg';
          const safeExt = fileExt.replace(/[^a-z0-9]/gi, '');
          const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${safeExt}`;
          const filePath = `${user?.id || 'public'}/${fileName}`;

          const { error: uploadError } = await uploadImageWithTimeout(img.file, filePath);

          if (uploadError) {
            console.error("Storage Error:", uploadError);
            throw new Error("Failed to upload image: " + (uploadError.message || "Unknown error"));
          }

          const { data } = supabase.storage
            .from('property-images')
            .getPublicUrl(filePath);

          uploadedUrls.push(data.publicUrl);
        } else {
          uploadedUrls.push(img.url);
        }
      }

      // 2. Save Property Data
      setStatus('saving_data');
      setStatusMessage(initialData ? 'Updating property listing...' : 'Finalizing property listing...');

      // Finalize Room Data (Capture any unsaved edits in the room sub-form)
      let finalRooms = [...rooms];
      if (newRoom.name) {
        const roomPrice = newRoom.pricingType === 'per_room'
          ? parseFloat(newRoom.price || '0')
          : (beds.length > 0 ? Math.min(...beds.map(b => parseFloat(b.price || '0'))) : 0);

        const finalizedRoom = {
          name: newRoom.name,
          price: roomPrice,
          description: newRoom.description,
          available: true,
          pricingType: newRoom.pricingType,
          beds: newRoom.pricingType === 'per_bed' ? beds.map((b, i) => ({
            id: b.name.toLowerCase().replace(/\s+/g, '-') + '-' + i,
            name: b.name,
            price: parseFloat(b.price || '0'),
            available: true
          })) : undefined
        };

        if (editingRoomId) {
          finalRooms = finalRooms.map(r => r.id === editingRoomId ? { ...r, ...finalizedRoom } : r);
        } else {
          finalRooms.push({ ...finalizedRoom, id: `room-${Date.now()}` });
        }
      }

      const propertyPayload = {
        title: formData.title,
        location: formData.location,
        locationName: formData.locationName,
        latitude: formData.latitude ? parseFloat(formData.latitude) : undefined,
        longitude: formData.longitude ? parseFloat(formData.longitude) : undefined,
        price: Number(formData.price),
        type: formData.type as any,
        bedrooms: Number(formData.bedrooms),
        bathrooms: Number(formData.bathrooms),
        description: formData.description,
        image: uploadedUrls[0],
        images: uploadedUrls,
        amenities: selectedAmenities,
        rooms: finalRooms, // Save finalized rooms array
        beds: propertyBeds.map(b => ({
          id: b.id,
          name: b.name,
          price: parseFloat(b.price || '0'),
          available: b.available
        })),
        coAgentId: formData.coAgentId || undefined
      };

      let result;
      if (initialData) {
        result = await updateProperty(initialData.id, propertyPayload);
      } else {
        result = await addProperty({
          ...propertyPayload,
          agentId: user?.id || 'unknown',
        });
      }

      if (result.error) {
        if (result.error.message && result.error.message.includes('invalid input value for enum property_status')) {
          throw new Error("Database Schema Mismatch: Please check enum types.");
        }
        throw new Error(result.error.message || "Database operation failed.");
      }

      setCreatedPropertyId(initialData ? initialData.id : (result as any).data?.[0]?.id);
      setStatus('success');
      setStatusMessage(initialData ? 'Property updated successfully!' : 'Property published successfully!');

    } catch (error: any) {
      console.error("Process failed:", error);
      setStatus('error');
      setErrorMessage(error.message || "An unexpected error occurred.");
    }
  };

  if (status === 'success') {
    return (
      <Card className="p-8 text-center animate-fadeIn">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{initialData ? 'Listing Updated!' : 'Listing Published!'}</h3>
        <p className="text-gray-600 mb-8">
          Your property <strong>{formData.title}</strong> has been {initialData ? 'updated' : 'published'} on Indigo Beans.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button variant="outline" onClick={onSuccess}>Back to Dashboard</Button>
          {createdPropertyId && (
            <Button onClick={() => window.location.href = `#/property/${createdPropertyId}`}>
              View Property <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </Card>
    );
  }

  if (status === 'uploading_images' || status === 'saving_data') {
    return (
      <Card className="p-12 text-center animate-fadeIn">
        <Loader2 className="w-16 h-16 text-[#2f5c96] animate-spin mx-auto mb-6" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">{statusMessage}</h3>
        <p className="text-gray-500 text-sm">Please do not close this window.</p>

        <div className="w-full max-w-md mx-auto mt-8 bg-gray-100 rounded-full h-2">
          <div
            className="bg-[#2f5c96] h-2 rounded-full transition-all duration-500"
            style={{ width: status === 'uploading_images' ? '60%' : '90%' }}
          ></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-gray-900">{initialData ? 'Edit Property Listing' : 'Add New Property Listing'}</h3>
        {initialData && (
          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">Edit Mode</span>
        )}
      </div>

      {status === 'error' && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <X className="w-5 h-5 text-red-500 mt-0.5" />
          <div>
            <h4 className="text-sm font-bold text-red-800">Upload Failed</h4>
            <p className="text-sm text-red-700">{errorMessage}</p>
            <button onClick={() => setStatus('idle')} className="text-xs text-red-600 underline mt-1 flex items-center">
              <RefreshCw className="w-3 h-3 mr-1" /> Try Again
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Image Upload Section */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">Property Images (Max 10)</label>
            <span className="text-xs text-gray-500">{images.length}/10 uploaded</span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {images.map((img, idx) => (
              <div key={idx} className={`relative rounded-lg overflow-hidden h-24 border-2 ${primaryIndex === idx ? 'border-[#2f5c96] ring-2 ring-blue-100' : 'border-gray-200'}`}>
                <img src={img.url} alt={`Preview ${idx}`} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/30 opacity-0 hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                  <button type="button" onClick={() => setPrimaryIndex(idx)} className="text-white hover:text-yellow-400" title="Make Primary">
                    <Star className={`w-5 h-5 ${primaryIndex === idx ? 'fill-yellow-400 text-yellow-400' : ''}`} />
                  </button>
                  <button type="button" onClick={() => removeImage(idx)} className="text-white hover:text-red-400" title="Remove">
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
                {primaryIndex === idx && <div className="absolute top-0 right-0 bg-[#2f5c96] text-white text-[10px] px-1">Main</div>}
              </div>
            ))}

            {images.length < 10 && (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg h-24 flex flex-col items-center justify-center cursor-pointer hover:border-[#2f5c96] hover:bg-blue-50/30 transition-all text-gray-400 hover:text-[#2f5c96]"
              >
                <Upload className="w-6 h-6 mb-1" />
                <span className="text-xs">Add Photo</span>
              </div>
            )}
          </div>
          <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" multiple />
        </div>

        {/* Basic Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Property Title</label>
            <input required type="text" className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} placeholder="e.g. Sunny Student Studio" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Location Name (e.g. Building or Spot Name)</label>
            <input type="text" className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none" value={formData.locationName} onChange={e => setFormData({ ...formData, locationName: e.target.value })} placeholder="e.g. Pimville Rooms" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Property Type</label>
            <select className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none bg-white" value={formData.type} onChange={e => setFormData({ ...formData, type: e.target.value })}>
              <option value="Studio">Studio</option>
              <option value="Apartment">Apartment</option>
              <option value="House">House</option>
              <option value="Dorm">Dorm</option>
            </select>
          </div>
        </div>

        {/* Co-Management (For Admins) */}
        {user?.role === UserRole.ADMIN && (
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 animate-fadeIn">
            <label className="block text-sm font-bold text-purple-900 mb-1 flex items-center gap-2">
              <UserCheck className="w-4 h-4" /> Secondary Agent (Third Party Co-Manager)
            </label>
            <p className="text-xs text-purple-700 mb-3">Both agents will be able to manage this property and see its messages.</p>
            <select
              className="w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-purple-500 outline-none bg-white text-sm"
              value={formData.coAgentId}
              onChange={e => setFormData({ ...formData, coAgentId: e.target.value })}
            >
              <option value="">None (Single Agent)</option>
              {users.filter(u => (u.role === UserRole.AGENT || u.role === UserRole.ADMIN) && u.id !== user?.id).map(u => (
                <option key={u.id} value={u.id}>{u.name}{u.role === UserRole.ADMIN ? ' (Admin)' : ''}</option>
              ))}
            </select>
          </div>
        )}

        {/* Price & Specs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Base Monthly Price (R)</label>
            <input required type="number" className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Total Bedrooms</label>
            <input type="number" className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none" value={formData.bedrooms} onChange={e => setFormData({ ...formData, bedrooms: e.target.value })} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Bathrooms</label>
            <input type="number" className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none" value={formData.bathrooms} onChange={e => setFormData({ ...formData, bathrooms: e.target.value })} />
          </div>
        </div>

        {/* Room Configuration Section */}
        {parseInt(formData.bedrooms || '0') > 0 && (
          <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h4 className="text-sm font-bold text-gray-900">Room Configuration</h4>
                <p className="text-xs text-gray-500">Add individual rooms with unique names and pricing.</p>
              </div>
              <div className="text-xs text-gray-400">
                {rooms.length} rooms defined
              </div>
            </div>

            {/* List of configured rooms */}
            <div className="space-y-3 mb-4">
              {rooms.map((room) => (
                <div key={room.id} className="bg-white p-3 rounded border border-gray-200 flex justify-between items-center">
                  <div>
                    <p className="text-sm font-bold text-gray-800">{room.name}</p>
                    <p className="text-xs text-gray-500">{room.description || 'No description'}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#2f5c96]">
                      R{room.price} {room.pricingType === 'per_bed' ? '(from /bed)' : ''}
                    </span>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => handleEditRoom(room)} className="text-blue-500 hover:bg-blue-50 p-1.5 rounded" title="Edit Room">
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => handleRemoveRoom(room.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded" title="Remove Room">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Add/Edit Room Inputs */}
            <div className={`space-y-4 p-4 rounded border ${editingRoomId ? 'bg-yellow-50 border-yellow-200' : 'bg-white border-blue-100'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">{editingRoomId ? 'Edit Room Name' : 'New Room Name'}</label>
                  <input
                    type="text"
                    className="w-full p-2 text-sm border border-gray-300 rounded outline-none focus:border-[#2f5c96]"
                    placeholder="e.g. Master 2000"
                    value={newRoom.name}
                    onChange={e => setNewRoom({ ...newRoom, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Pricing Type</label>
                  <select
                    className="w-full p-2 text-sm border border-gray-300 rounded outline-none focus:border-[#2f5c96] bg-white"
                    value={newRoom.pricingType}
                    onChange={e => setNewRoom({ ...newRoom, pricingType: e.target.value as any })}
                  >
                    <option value="per_room">Per Room (Full unit)</option>
                    <option value="per_bed">Per Bed (Shared room)</option>
                  </select>
                </div>
              </div>

              {newRoom.pricingType === 'per_room' ? (
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Room Price (R)</label>
                  <input
                    type="number"
                    className="w-full p-2 text-sm border border-gray-300 rounded outline-none focus:border-[#2f5c96]"
                    placeholder="e.g. 1700"
                    value={newRoom.price}
                    onChange={e => setNewRoom({ ...newRoom, price: e.target.value })}
                  />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="block text-xs font-medium text-gray-500">Beds in this Room</label>
                    <button type="button" onClick={handleAddBed} className="text-xs text-[#2f5c96] font-bold flex items-center hover:underline">
                      <Plus className="w-3 h-3 mr-1" /> Add Bed
                    </button>
                  </div>
                  <div className="space-y-2">
                    {beds.map((bed, bIdx) => (
                      <div key={bIdx} className="flex gap-2 items-center">
                        <input
                          type="text"
                          className="flex-1 p-2 text-sm border border-gray-300 rounded outline-none focus:border-[#2f5c96]"
                          placeholder="Bed Name (e.g. Bed 1)"
                          value={bed.name}
                          onChange={(e) => handleBedChange(bIdx, 'name', e.target.value)}
                        />
                        <input
                          type="number"
                          className="w-32 p-2 text-sm border border-gray-300 rounded outline-none focus:border-[#2f5c96]"
                          placeholder="Price"
                          value={bed.price}
                          onChange={(e) => handleBedChange(bIdx, 'price', e.target.value)}
                        />
                        <button type="button" onClick={() => handleRemoveBed(bIdx)} className="text-red-500 p-1 hover:bg-red-50 rounded">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    {beds.length === 0 && (
                      <p className="text-xs text-gray-400 italic">No beds added yet. Click "Add Bed".</p>
                    )}
                  </div>
                </div>
              )}

              <div className="md:col-span-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Description (Optional)</label>
                <div className="flex gap-2">
                  <input type="text" className="flex-1 p-2 text-sm border border-gray-300 rounded outline-none focus:border-[#2f5c96]" placeholder="e.g. En-suite, balcony access..." value={newRoom.description} onChange={e => setNewRoom({ ...newRoom, description: e.target.value })} />
                  {editingRoomId ? (
                    <div className="flex gap-2">
                      <Button type="button" size="sm" onClick={handleUpdateRoom} disabled={!newRoom.name}>
                        <Save className="w-4 h-4 mr-1" /> Update
                      </Button>
                      <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingRoomId(null); setNewRoom({ name: '', price: '', description: '', pricingType: 'per_room' }); setBeds([]); }}>
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <Button type="button" size="sm" onClick={handleAddRoom} disabled={!newRoom.name}>
                      <Plus className="w-4 h-4 mr-1" /> Add
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bed Configuration Section (Property-wide) */}
        <div className="bg-purple-50/50 p-4 rounded-lg border border-purple-100">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h4 className="text-sm font-bold text-gray-900">General Bed Configuration</h4>
              <p className="text-xs text-gray-500">Add beds available in the property (not strictly tied to a specific room).</p>
            </div>
            <div className="text-xs text-gray-400">
              {propertyBeds.length} beds defined
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {propertyBeds.map((bed) => (
              <div key={bed.id} className="bg-white p-3 rounded border border-gray-200 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${bed.available ? 'bg-green-500' : 'bg-red-500'}`} />
                  <span className="text-sm font-medium text-gray-800">{bed.name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-purple-700">R{bed.price}</span>
                  <div className="flex gap-1">
                    <button type="button" onClick={() => togglePropBedAvailability(bed.id)} className={`p-1.5 rounded transition-colors ${bed.available ? 'text-green-600 hover:bg-green-50' : 'text-red-400 hover:bg-red-50'}`} title={bed.available ? "Mark as Rented" : "Mark as Available"}>
                      {bed.available ? <CheckCircle className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    </button>
                    <button type="button" onClick={() => handleRemovePropBed(bed.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded" title="Remove Bed">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white p-4 rounded border border-purple-100 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Bed Name</label>
                <input
                  type="text"
                  className="w-full p-2 text-sm border border-gray-300 rounded outline-none focus:border-purple-500"
                  placeholder="e.g. Bed A"
                  value={newPropBed.name}
                  onChange={e => setNewPropBed({ ...newPropBed, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Price (R)</label>
                <input
                  type="number"
                  className="w-full p-2 text-sm border border-gray-300 rounded outline-none focus:border-purple-500"
                  placeholder="e.g. 1500"
                  value={newPropBed.price}
                  onChange={e => setNewPropBed({ ...newPropBed, price: e.target.value })}
                />
              </div>
            </div>
            <Button type="button" size="sm" className="w-full bg-purple-600 hover:bg-purple-700" onClick={handleAddPropBed} disabled={!newPropBed.name}>
              <Plus className="w-4 h-4 mr-1" /> Add General Bed
            </Button>
          </div>
        </div>

        {/* Location Section */}
        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 space-y-4">
          <h4 className="text-sm font-bold text-gray-900 flex items-center"><MapPin className="w-4 h-4 mr-2 text-[#2f5c96]" /> Location & Map</h4>
          <p className="text-xs text-gray-500 -mt-2">Drag the red pointer or tap on the map to set your exact location. The address will update automatically.</p>

          <div className="relative">
            <label className="block text-xs font-medium text-gray-500 mb-1">Address Search (Auto-filled on map change)</label>
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400 w-4 h-4" />
              <input
                type="text"
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2f5c96] outline-none transition-colors"
                value={formData.location}
                onChange={handleAddressInput}
                placeholder="Start typing or move pin on map..."
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              />
              {(isSuggesting || isReverseGeocoding) && (
                <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-[#2f5c96]" />
              )}
            </div>

            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-10 w-full bg-white border border-gray-200 rounded-md shadow-lg mt-1 max-h-60 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    type="button"
                    className="w-full text-left px-4 py-2 hover:bg-blue-50 text-sm text-gray-700 border-b border-gray-50 last:border-0"
                    onClick={() => selectSuggestion(s)}
                  >
                    <div className="font-medium">{s.address}</div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 items-end">
            <div className="flex-1 grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-500">Lat</label>
                <input type="text" className="w-full p-2 text-xs border border-gray-300 rounded bg-white text-gray-500" value={formData.latitude} readOnly placeholder="-25..." />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500">Lng</label>
                <input type="text" className="w-full p-2 text-xs border border-gray-300 rounded bg-white text-gray-500" value={formData.longitude} readOnly placeholder="28..." />
              </div>
            </div>
            <Button type="button" size="sm" variant="ghost" className="text-[#2f5c96] h-[34px]" onClick={handleGetCurrentLocation} disabled={isLocating}>
              {isLocating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Navigation className="w-3 h-3" />}
              <span className="ml-1 text-xs">My Loc</span>
            </Button>
          </div>

          <div className="h-64 w-full rounded-lg border border-gray-300 overflow-hidden relative z-0">
            <MapContainer
              center={mapCenter}
              zoom={13}
              scrollWheelZoom={false}
              doubleClickZoom={false}
              className="h-full w-full cursor-crosshair"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <MapUpdater center={mapCenter} />
              <MapEventsHandler onLocationSelect={handleMapClickHandler} />

              {formData.latitude && formData.longitude ? (
                <DraggableMarker
                  position={[parseFloat(formData.latitude), parseFloat(formData.longitude)]}
                  onDragEnd={handleMarkerDragEnd}
                />
              ) : (
                <Marker position={mapCenter} icon={defaultIcon} opacity={0.5} />
              )}
            </MapContainer>

            {!formData.latitude && (
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 bg-white/90 px-3 py-1 rounded-full text-xs shadow-md z-[400] pointer-events-none text-red-600 font-medium">
                Tap map or use search to set location
              </div>
            )}
          </div>
        </div>

        {/* Features Checkboxes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Features & Amenities</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {AMENITIES_LIST.map(amenity => (
              <label key={amenity} className="flex items-center space-x-2 cursor-pointer p-2 border border-gray-200 rounded-lg hover:bg-gray-50">
                <input
                  type="checkbox"
                  className="rounded text-[#2f5c96] focus:ring-[#2f5c96]"
                  checked={selectedAmenities.includes(amenity)}
                  onChange={() => toggleAmenity(amenity)}
                />
                <span className="text-sm text-gray-700">{amenity}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea rows={3} className="mt-1 block w-full rounded-md border-gray-300 border px-3 py-2 focus:ring-2 focus:ring-[#2f5c96] outline-none" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} placeholder="Describe the property..."></textarea>
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" className="min-w-[140px] flex items-center">
            {initialData ? <><Save className="w-4 h-4 mr-2" /> Update Listing</> : 'Publish Listing'}
          </Button>
        </div>
      </form>
    </Card>
  );
}